<template>
  <div class="flex flex-col gap-y-5 items-start w-full">
    <logic-group
      v-for="(group, index) in filter?.logicGroups ?? []"
      :key="group['id']"
      v-model="filter.logicGroups[index]"
      class="w-full"
      :statement-list="statementList"
      :errors="errors?.[group.id]"
      :disabled="disabled"
      @remove="removeGroup"
    />
    <button
      v-if="!disabled"
      type="button"
      class="text-body-regular !text-info-blue"
      @click="addGroup"
    >
      + AND
    </button>
  </div>
</template>

<script lang="ts" setup>
import { useVModel } from '@vueuse/core';
import { v4 as uuidv4 } from 'uuid';
import { computed, ref, watch } from 'vue';
import { fetchApi } from '@hooks/fetch';
import LogicGroup from './LogicGroup.vue';
import type {
  Filter, FilterPrototypeList, LogicStatement, MaybeHydra,
} from '@/entities/Entities';

interface Props {
  modelValue?: MaybeHydra<Filter>;
  route: string;
  disabled?: boolean;
  hideConditional?: boolean;
  hideConditionalLicense?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => ({
    logicGroups: [],
  }),
});

const rawStatementList = ref<FilterPrototypeList | undefined>(undefined);

const loadStatements = async () => {
  rawStatementList.value = await fetchApi<FilterPrototypeList>(`${props.route}/prototypes`);
};

const statementList = computed(() => {
  let entries = Object.entries(rawStatementList.value ?? {});
  if (props.hideConditional) {
    entries = entries.filter((item) => !item[1].declaration.conditional);
  }
  if (props.hideConditionalLicense) {
    entries = entries.filter((item) => !item[1].declaration.conditionalLicense);
  }
  return Object.fromEntries(entries);
});

loadStatements();

const emit = defineEmits(['update:modelValue']);
const filter = useVModel(props, 'modelValue', emit, { passive: true });

const addGroup = () => {
  filter.value?.logicGroups?.push({
    id: uuidv4(),
    logicStatements: [
      {
        id: uuidv4(),
        name: undefined,
        data: {},
      },
    ],
  });
  emit('update:modelValue', filter.value);
};

const removeGroup = (id: string) => {
  filter.value.logicGroups = filter.value?.logicGroups?.filter(
    (group: LogicGroup) => group.id !== id,
  );
  emit('update:modelValue', filter.value);
};

watch(
  () => props.hideConditional,
  (newVal) => {
    if (newVal === true) {
      filter.value.logicGroups.forEach((group) => {
        group.logicStatements = group.logicStatements.filter(
          (statement) => !rawStatementList.value?.[statement.name]?.declaration.conditional,
        );
        if (group.logicStatements.length === 0 && group.id) {
          removeGroup(group.id);
        }
      });
    }
  },
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const errors = ref<Record<string, Record<string, object>> | undefined>(undefined);

const validateData = (data: MaybeHydra<Filter>): boolean => {
  errors.value = undefined;
  let out = true;
  data.logicGroups.forEach((group: MaybeHydra<LogicGroup>) =>
    group.logicStatements.forEach((statement: MaybeHydra<LogicStatement>) => {
      if (!statement.name) {
        out = false;
        errors.value = {
          ...errors.value,
          [group.id]: {
            ...errors.value?.[group.id],
            [statement.id ?? '']: {
              ...errors.value?.[group.id]?.[statement.id ?? ''],
              missingStatement: 'Missing Statement!',
            },
          },
        };
        return;
      }
      const prototype = statementList.value?.[statement.name];
      if (!prototype) {
        out = false;
        return;
      }
      Object.keys(prototype.declaration.data).forEach((key: string) => {
        const dataType = prototype.declaration.data[key].split('|');
        if (!dataType.includes(typeof statement.data?.[key])) {
          out = false;
          errors.value = {
            ...errors.value,
            [group.id]: {
              ...errors.value?.[group.id],
              [statement.id ?? '']: {
                ...errors.value?.[group.id]?.[statement.id ?? ''],
                [key]: 'Wrong input!',
              },
            },
          };
        }
      });
    }));
  return out;
};

const checkForUnconditional = (data: MaybeHydra<Filter>): boolean =>
  data.logicGroups.every((group) =>
    group.logicStatements.some((item) => !statementList.value?.[item.name]?.declaration.conditional && !statementList.value?.[item.name]?.declaration.conditionalLicense));

defineExpose({ validateData, checkForUnconditional });

watch(
  () => filter.value,
  () => {
    errors.value = undefined;
  },
  { deep: true },
);
</script>

<style lang="scss" scoped></style>
